import { Component, Input, OnChanges, OnInit } from '@angular/core'
import { Router } from '@angular/router'

import { AuthenticationService } from '@/services/authentication.service'
import { ServiceProduct } from '@/domain/service-product'

import { Rack } from '@/domain/rack'
import { SalesPeriod } from '@/domain/sales-period'
import { ServiceProductVariation } from '@/domain/service-product-variation'
import { Store } from '@/domain/store'
import { Seller } from '@/domain/seller'

import { SelectedVariationAndDate } from '@/model/calendar'

@Component({
  selector: 'sales-period-list',
  templateUrl: './sales-period-list.component.html'
})
export class SalesPeriodListComponent implements OnInit, OnChanges {

  @Input()
  salesPeriods: SalesPeriod[] = null

  @Input()
  title: string = null

  @Input()
  approvalActions: boolean = false

  /** List of columns to hide */
  @Input()
  hide: string = null

  /** Whether to include a 'create new sales period' button */
  @Input()
  create: boolean = false

  /** Filter by specific seller */
  @Input()
  sellerFilter: Seller = null

  private _salesPeriods: SalesPeriod[] = null
  private _hasManyStores: boolean = false

  preselectStoreId: number = null

  searchField: string = ''
  showCurrentFilter: boolean = false
  filteredPeriods: SalesPeriod[] = null

  filterStore: Store = null
  filterProduct: ServiceProduct = null
  filterVariation: ServiceProductVariation = null
  filterRack: Rack = null

  openSalesPeriodModal: boolean = false
  selection: SelectedVariationAndDate = null

  constructor(
    public auth: AuthenticationService,
    private _router: Router
  ) {
    // Subscribe to user data changes only, not token-only updates
    // This prevents unnecessary re-evaluations during token refresh
    this.auth.userDataChanges$.subscribe(user => {
      // on logout, user is undefined
      this._hasManyStores = user && user.merchant && user.merchant.stores && user.merchant.stores.length > 1
    })
  }

  get multistore(): boolean {
    return this._hasManyStores
  }

  get maybeStoreName(): string {
    if (this.multistore && this.auth.isMerchant && this.filterStore) {
      return this.filterStore.name
    }
    return null
  }

  ngOnInit(): void {
    this._initFilterSelections()
    this._refresh()
  }

  private _initFilterSelections(): void {
    let storeId = sessionStorage.getItem('sales-periods.store_id')
    if (storeId && !isNaN(+storeId)) {
      this.preselectStoreId = +storeId
    }
  }

  ngOnChanges(): void {
    this._refresh()
  }

  openSalePeriodModal(): void {
    const product = this.filterProduct
    const variation = this.filterVariation
    if (this.filterProduct || this.filterVariation) {
      this.selection = new SelectedVariationAndDate(product, variation, this.filterRack, null, null)
    } else {
      this.selection = null
    }
    this.openSalesPeriodModal = true
  }

  salesPeriodModalClosed($event): void {
    this.selection = null
    this.openSalesPeriodModal = false
  }

  filterChanged($event: any): void {
    // Persist store selection to sessionStorage
    if (this.filterStore) {
      sessionStorage.setItem('sales-periods.store_id', this.filterStore.id.toString())
    } else {
      sessionStorage.removeItem('sales-periods.store_id')
    }
    this._refresh()
  }

  salesPeriodCreated(period: SalesPeriod): void {
    this._router.navigate([ '/period', period.id ])
  }

  // private

  private _refresh(): void {
    this._salesPeriods = this.salesPeriods
    if (this._salesPeriods) {
      let sellerId = null;

      if (this.sellerFilter) {
        // Use the specific seller filter if provided
        sellerId = this.sellerFilter.id;
      } else if (this.auth.isSeller) {
        // Otherwise use the current user's seller ID if they are a seller
        sellerId = this.auth.currentUserValue?.seller?.id;
      }

      this.filteredPeriods = this._salesPeriods.filter(period => {
        return (!sellerId || period.seller_id == sellerId)
            && (!this.filterStore || period.store.id === this.filterStore.id)
            && (!this.filterProduct || period.service_product.id === this.filterProduct.id)
            && (!this.filterVariation || period.service_product_variation.id === this.filterVariation.id)
            && (!this.filterRack || period.rack_time_slot.reck.id === this.filterRack.id)
      })

      this.showCurrentFilter = true
    }
  }
}
